import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArchiveRestore, ChevronRight, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import {
    useProgramBank,
    useCreateProgramBankEntry,
    useUpdateProgramBankEntry,
    useDeleteProgramBankEntry,
    useRestoreProgramBankEntry,
    TEACHING_METHODS,
    type ProgramBankEntry,
    type ProgramBankWritePayload,
} from '@/api/programBank';
import { useProgramDomains } from '@/api/programDomains';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';

export function ProgramBankPanel() {
    const navigate = useNavigate();
    const canEdit = useAuthStore((s) => s.hasPermission('edit_treatment_plans'));
    const [domainFilter, setDomainFilter] = useState<string>('');
    const [search, setSearch] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [editing, setEditing] = useState<ProgramBankEntry | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmArchive, setConfirmArchive] = useState<ProgramBankEntry | null>(null);

    const { data: domains } = useProgramDomains();
    const { data, isLoading, isError, error } = useProgramBank({
        programDomainId: domainFilter || undefined,
        includeTargets: true,
        includeArchived: showArchived,
    });
    const createM = useCreateProgramBankEntry();
    const updateM = useUpdateProgramBankEntry();
    const deleteM = useDeleteProgramBankEntry();
    const restoreM = useRestoreProgramBankEntry();

    const activeDomains = useMemo(
        () => (domains ?? [])
            .filter((d) => d.status === 'Active')
            .sort((a, b) => a.name.localeCompare(b.name)),
        [domains],
    );

    const rows = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data;
        return data.filter((r) => r.name.toLowerCase().includes(q));
    }, [data, search]);

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-text-subtle">
                    Practice-wide starting-point programs and targets. Copying one to a client
                    duplicates the fields — subsequent edits don't flow back.
                </p>
                {canEdit && (
                    <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                        <Plus className="w-3.5 h-3.5" /> Add Program
                    </Button>
                )}
            </div>

            <Card className="p-0 overflow-hidden mt-3">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div className="relative flex-1 min-w-[220px] max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-subtle" />
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name…"
                            aria-label="Search programs by name"
                            className="pl-8 h-8 py-1"
                        />
                    </div>
                    <select
                        value={domainFilter}
                        onChange={(e) => setDomainFilter(e.target.value)}
                        aria-label="Filter by program domain"
                        className="h-8 px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                    >
                        <option value="">All domains</option>
                        {activeDomains.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                    <label className="flex items-center gap-1.5 text-xs text-text-muted select-none cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                        />
                        Show archived
                    </label>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'program' : 'programs'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load program bank</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>{data && data.length > 0 ? 'No programs match the current filters.' : 'No programs in the bank yet.'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold">Domain</th>
                                    <th className="px-3 py-2 font-bold">Teaching method</th>
                                    <th className="px-3 py-2 font-bold w-24 text-right">Targets</th>
                                    {canEdit && <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>}
                                    <th className="px-3 py-2 w-6" aria-hidden />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.id}
                                        onClick={() => navigate(`/treatment-plans/programs/${r.id}`)}
                                        className={`border-b border-border last:border-b-0 transition-colors group cursor-pointer ${r.archived ? 'opacity-60 hover:bg-surface-muted' : 'hover:bg-surface-muted'
                                            }`}
                                    >
                                        <td className="px-3 py-2 font-medium text-text">
                                            {r.name}
                                            {r.archived && (
                                                <span className="ml-2 text-2xs uppercase tracking-wider text-text-subtle">Archived</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">{r.programDomain || '—'}</td>
                                        <td className="px-3 py-2 text-text-muted">{r.teachingMethod || '—'}</td>
                                        <td className="px-3 py-2 text-right text-text-muted tabular-nums">
                                            {r.targets?.length ?? 0}
                                        </td>
                                        {canEdit && (
                                            <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                                                {r.archived ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="xs"
                                                        aria-label={`Restore ${r.name}`}
                                                        disabled={restoreM.isPending}
                                                        onClick={() => restoreM.mutate(r.id)}
                                                    >
                                                        <ArchiveRestore className="w-3 h-3" />
                                                    </Button>
                                                ) : (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            aria-label={`Edit ${r.name}`}
                                                            onClick={() => setEditing(r)}
                                                            className="mr-1"
                                                        >
                                                            <Pencil className="w-3 h-3" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            aria-label={`Archive ${r.name}`}
                                                            onClick={() => setConfirmArchive(r)}
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                    </>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-2 py-2 text-text-subtle">
                                            <ChevronRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <ProgramBankEntryDialog
                    initial={editing}
                    activeDomains={activeDomains}
                    onClose={() => {
                        setCreating(false);
                        setEditing(null);
                    }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {confirmArchive && (
                <ArchiveConfirmDialog
                    row={confirmArchive}
                    onClose={() => setConfirmArchive(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(confirmArchive.id);
                        setConfirmArchive(null);
                    }}
                />
            )}
        </div>
    );
}

function ProgramBankEntryDialog({
    initial,
    activeDomains,
    onClose,
    onSubmit,
}: {
    initial: ProgramBankEntry | null;
    activeDomains: { id: string; name: string }[];
    onClose: () => void;
    onSubmit: (p: ProgramBankWritePayload) => Promise<void>;
}) {
    const [name, setName] = useState(initial?.name ?? '');
    const [domainId, setDomainId] = useState(initial?.programDomainId ?? '');
    const [teachingMethod, setMethod] = useState(initial?.teachingMethod ?? '');
    const [description, setDescription] = useState(initial?.description ?? '');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }
        setSaving(true);
        try {
            await onSubmit({
                name: name.trim(),
                programDomainId: domainId || null,
                teachingMethod: teachingMethod.trim(),
                description: description.trim(),
            });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit Program' : 'Add Program'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <FormField label="Name" required error={err ?? undefined}>
                        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                    </FormField>
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Domain">
                            {(id) => (
                                <select
                                    id={id}
                                    value={domainId ?? ''}
                                    onChange={(e) => setDomainId(e.target.value)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="">— None —</option>
                                    {activeDomains.map((d) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                        <FormField label="Teaching method">
                            {(id) => (
                                <select
                                    id={id}
                                    value={teachingMethod}
                                    onChange={(e) => setMethod(e.target.value)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="">— None —</option>
                                    {TEACHING_METHODS.map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            )}
                        </FormField>
                    </div>
                    <FormField label="Description">
                        {(id) => <Textarea id={id} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />}
                    </FormField>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ArchiveConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: ProgramBankEntry;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const targetCount = row.targets?.length ?? 0;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Archive program?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Archive <span className="font-medium text-text">{row.name}</span> from the
                    program bank? {targetCount > 0 && (
                        <>Its {targetCount} target{targetCount === 1 ? '' : 's'} stay visible under
                            the archived program. </>
                    )}
                    Client programs already copied from this entry keep their own data and
                    aren't affected.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try {
                                await onConfirm();
                            } catch (e) {
                                setErr(extractErrorMessage(e, 'Could not archive.'));
                            } finally {
                                setWorking(false);
                            }
                        }}
                    >
                        {working ? 'Archiving…' : 'Archive'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
